/**
 * Shared date-picker behaviour (browser only): the flatpickr options and the typing mask
 * every date field uses — task/project modals, mass edit, share expiry, the Program
 * Roadmap frame. The format itself lives in ./date.js; nothing here hardcodes it.
 */
import { FLATPICKR_DATE_FORMAT, isCompleteDisplayDate, isDateInputChar, maskDisplayDate, parseDisplayDate } from './date.js';

/**
 * flatpickr options in the app-wide format: typing allowed, no native mobile picker,
 * weeks start on Monday.
 * @param {object|null} locale - flatpickr locale object (e.g. the Spanish l10n), or null for English
 * @param {object} [extra] - any other flatpickr options (they win over the defaults)
 */
export function datePickerOptions(locale, extra = {}) {
    return {
        dateFormat: FLATPICKR_DATE_FORMAT,
        allowInput: true,
        disableMobile: true,
        locale: { ...(locale || {}), firstDayOfWeek: 1 },
        ...extra,
    };
}

/**
 * Typing mask for a date field: digits only, separators inserted as you type, day/month
 * clamped. Typing a digit into a complete date starts over; Backspace at the very start
 * clears the field.
 * @param {HTMLInputElement} input
 * @param {{ onComplete?: (iso: string) => void }} [options] - called with the ISO date
 *        whenever the typed text becomes a complete, real date
 * @returns {{ reset: () => void }} reset the "start over" state (e.g. after a calendar pick)
 */
export function attachDateMask(input, { onComplete } = {}) {
    // Touch screens pick from the calendar; the on-screen keyboard just covers it.
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
        input.inputMode = 'none';
    }
    let clearedOnFirstKey = false;
    input.addEventListener('keydown', (event) => {
        if (!clearedOnFirstKey && isCompleteDisplayDate(input.value) && event.key.length === 1 && /\d/.test(event.key)) {
            input.value = '';
            clearedOnFirstKey = true;
        }
        if (event.key === 'Backspace' && input.selectionStart === 0 && input.selectionEnd === 0) {
            input.value = '';
            clearedOnFirstKey = true;
            event.preventDefault();
        }
    });
    input.addEventListener('input', () => {
        const formatted = maskDisplayDate(input.value);
        if (formatted !== input.value) input.value = formatted;
        const iso = parseDisplayDate(formatted);
        if (iso && onComplete) onComplete(iso);
    });
    input.addEventListener('keypress', (event) => {
        if (event.key.length === 1 && !isDateInputChar(event.key)) event.preventDefault();
    });
    input.addEventListener('blur', () => { clearedOnFirstKey = false; });
    return { reset: () => { clearedOnFirstKey = false; } };
}
